import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { productsApi } from '../../../api';
import { useCart } from '../../../contexts/CartContext';
import { useDialog } from '../../../contexts/DialogContext';
import { toast } from 'react-hot-toast';
import Dialog from '../../../components/ui/Dialog';

const FeaturedCollections = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingProductId, setAddingProductId] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState({});
  const { addToCart, cartItems } = useCart();
  const navigate = useNavigate();
  
  // Vérifier si un produit est déjà dans le panier
  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  // State for the dialog
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDialogSize, setSelectedDialogSize] = useState('');
  const { isDialogOpen, setIsDialogOpen } = useDialog();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);

  // Update images array when selectedProduct changes
  useEffect(() => {
    if (selectedProduct) {
      const productImages = [
        selectedProduct.image,
        selectedProduct.hoverImage
      ].filter(Boolean);
      setImages(productImages);
      setCurrentImageIndex(0);
    }
  }, [selectedProduct]);

  // Navigation functions for image slider
  const handleNextImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrevImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  // Fonction utilitaire pour afficher la notification "Déjà dans le panier"
  const showAlreadyInCartToast = () => {
    toast.dismiss(); // Fermer les notifications précédentes
    toast((t) => (
      <div className="flex items-center space-x-3">
        <div className="bg-white p-1 rounded-full">
          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div>
          <p className="font-semibold text-white">Déjà dans le panier</p>
          <p className="text-sm text-white/90">Ce produit est déjà dans votre panier</p>
        </div>
      </div>
    ), {
      position: 'bottom-center',
      duration: 2000,
      style: {
        background: '#3B82F6',
        color: '#fff',
        padding: '16px 24px',
        borderRadius: '12px',
        maxWidth: '90%',
        width: 'auto',
        margin: '0 auto 20px',
        zIndex: 9999
      },
      className: 'toast-notification'
    });
  };

  // Fonction utilitaire pour afficher la notification de succès
  const showSuccessToast = () => {
    toast.dismiss();
    toast((t) => (
      <div className="flex items-center space-x-3">
        <div className="bg-white p-1 rounded-full">
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div>
          <p className="font-semibold text-white">Produit ajouté !</p>
          <p className="text-sm text-white/90">Votre article a été ajouté au panier</p>
        </div>
      </div>
    ), {
      position: 'bottom-center',
      duration: 3000,
      style: {
        background: '#10B981',
        color: '#fff',
        padding: '16px 24px',
        borderRadius: '12px',
        maxWidth: '90%',
        width: 'auto',
        margin: '0 auto 20px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      },
      className: 'animate-bounce'
    });
  };

  // Handlers for the dialog
  const handleProductClick = (product) => {
    setSelectedProduct({
      ...product,
      // Sélectionner la première taille disponible par défaut
      selectedSize: product.availableSizes?.[0] || 'UNIQUE'
    });
    setSelectedDialogSize(product.availableSizes?.[0] || 'UNIQUE');
    setCurrentImageIndex(0);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
    setAddingProductId(null);
    setCurrentImageIndex(0);
  };

  // Fonction pour mélanger un tableau
  const shuffleArray = (array) => {
    return array
      .map(value => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  // Charger 4 produits depuis l'API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await productsApi.list();
        const products = data.items || [];
        const random4 = shuffleArray(products).slice(0, 4);
        // Adapter les propriétés pour correspondre au composant
        const formatted = random4.map(product => ({
          id: product.id,
          title: product.name,
          subtitle: product.subtitle || "",
          description: product.description || "",
          price: parseFloat(product.price) || 0,
          originalPrice: product.originalPrice ? parseFloat(product.originalPrice) : null,
          image: product.urlImage,
          hoverImage: product.urlImageHover,
          badge: product.badge || "",
          badgeColor: product.badgeColor || "bg-accent",
          category: product.categories?.[0]?.name || "",
          inStock: product.stock > 0,
          stockCount: product.stock || 0,
          availableSizes: product.size ? product.size.split(',').map(s => s.trim()) : []
        }));
        setCollections(formatted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const imageVariants = {
    hover: { scale: 1.1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  // Mettre à jour la taille sélectionnée pour un produit
  const handleSizeSelect = (productId, size, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedSizes(prev => ({
      ...prev,
      [productId]: size
    }));
  };

  const handleAddToCart = async (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Vérifier si le produit est déjà dans le panier
    if (isInCart(product.id)) {
      showAlreadyInCartToast();
      return;
    }

    // Vérifier si une taille est sélectionnée
    const selectedSize = selectedSizes[product.id] || 
                        (product.availableSizes?.length ? product.availableSizes[0] : 'UNIQUE');

    // Empêcher les clics multiples
    if (addingProductId === product.id) return;
    
    // Désactiver le bouton immédiatement
    setAddingProductId(product.id);
    
    try {
      // Créer le payload du panier
      const cartItem = {
        id: product.id,
        name: product.title,
        price: product.price,
        originalPrice: product.originalPrice || null,
        image: product.image,
        size: selectedSize,
        color: 'Standard',
        source: 'From Featured Collection',
        promotion: product.promotion
      };
      
      // Ajouter au panier
      addToCart(cartItem);
      
      // Afficher la notification de succès
      showSuccessToast();
      
      // Mettre à jour l'état local après l'ajout au panier
      setAddingProductId(null);
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      toast.error("❌ Erreur lors de l'ajout au panier", {
        position: 'top-center',
        duration: 2000,
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '12px 24px',
          borderRadius: '8px',
          fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }
      });
    } finally {
      // Toujours réinitialiser l'état du bouton, même en cas d'erreur
      setAddingProductId(null);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <section className="py-16 lg:py-24 px-4 lg:px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-black text-3xl md:text-5xl lg:text-6xl text-foreground mb-4">
              NOUVEAUTÉS <span className="text-accent">URBANSPACE</span>
            </h2>
            <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto">
              Découvrez nos dernières collections exclusives. Pièces uniques, qualité supérieure, style urbain.
            </p>
          </motion.div>

          {/* Collections Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8"
          >
            {collections.map((collection) => (
              <motion.div
                key={collection.id}
                variants={cardVariants}
                className="relative group overflow-hidden rounded-lg bg-surface border border-street transition-all duration-300 hover:shadow-lg hover:shadow-accent/10 cursor-pointer"
                onMouseEnter={() => setHoveredCard(collection.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => handleProductClick(collection)}
              >
                {/* Product Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  <motion.div
                    variants={imageVariants}
                    whileHover="hover"
                    className="w-full h-full"
                  >
                    <Image
                      src={hoveredCard === collection.id && collection.hoverImage ? collection.hoverImage : collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover transition-street"
                    />
                  </motion.div>

                  {/* Badge */}
                  {collection.badge && (
                    <div className={`absolute top-4 left-4 ${collection.badgeColor} text-background px-3 py-1 rounded-full text-xs font-bold tracking-wide`}>
                      {collection.badge}
                    </div>
                  )}

                  {/* Stock Status */}
                  <div className="absolute top-4 right-4">
                    {collection.inStock ? (
                      <div className="bg-success text-success-foreground px-2 py-1 rounded text-xs font-bold">
                        {collection.stockCount} EN STOCK
                      </div>
                    ) : (
                      <div className="bg-error text-error-foreground px-2 py-1 rounded text-xs font-bold">
                        RUPTURE
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <div className="block mb-2">
                    <h3 className="font-heading font-bold text-lg text-foreground group-hover:text-accent transition-colors truncate">
                      {collection.title}
                    </h3>
                    {collection.description && (
                      <p className="text-sm text-muted-foreground truncate">{collection.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-accent font-bold">
                        {collection.price.toFixed(3)} TND
                        {collection.originalPrice && (
                          <span className="ml-2 text-muted-foreground line-through text-sm">
                            {collection.originalPrice.toFixed(3)} TND
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Add to Cart Button */}
                    <div className="mt-3">
                      <Button 
                        variant={isInCart(collection.id) ? 'ghost' : 'outline'}
                        className={`w-full transition-all ${
                          isInCart(collection.id) 
                            ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-default' 
                            : `border-accent text-accent hover:bg-accent hover:text-white ${
                                addingProductId === collection.id ? 'opacity-75 cursor-not-allowed' : ''
                              }`
                        }`}
                        onClick={(e) => handleAddToCart(collection, e)}
                        disabled={!collection.inStock || isInCart(collection.id) || addingProductId === collection.id}
                      >
                        {addingProductId === collection.id ? (
                          <span className="flex items-center justify-center text-lg">
                            <Icon name="Loader" className="h-6 w-6 animate-spin mr-3" />
                            Ajout...
                          </span>
                        ) : isInCart(collection.id) ? (
                          <span className="flex items-center justify-center text-lg">
                            
                            Déjà au panier
                          </span>
                        ) : (
                          <span className="flex items-center justify-center text-lg">
                            <Icon name="ShoppingBag" className="mr-3 h-6 w-6" />
                            panier
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to="/collections">
              <Button
                variant="outline"
                size="lg"
                className="border-accent text-accent hover:bg-accent hover:text-accent-foreground px-8 py-4 text-lg font-bold tracking-wide transition-street"
                iconName="ArrowRight"
                iconPosition="right"
              >
                TOUTES LES COLLECTIONS
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Product Detail Dialog */}
      {selectedProduct && (
          <Dialog
            isOpen={isDialogOpen}
            onClose={handleCloseDialog}
            title={selectedProduct.title}
            showCancel={false}
            showConfirm={false}
            className="max-w-4xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Enhanced Product Image Slider */}
              <div className="relative group aspect-[3/4] overflow-hidden rounded-lg md:rounded-lg w-full md:w-full mx-auto">
                {/* Main Image with smooth transition */}
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full"
                >
                  <Image
                    src={images.length > 0 ? images[currentImageIndex] : selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                {images.length > 1 && (
                  <>
                    {/* Navigation Arrows */}
                    <div className="absolute inset-0 flex items-center justify-between p-2">
                      {/* Previous Button */}
                      <motion.button
                        onClick={handlePrevImage}
                        className="h-10 w-10 flex items-center justify-center bg-white/90 text-gray-900 rounded-full shadow-lg hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-300 z-10"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Image précédente"
                      >
                        <Icon name="ChevronLeft" size={24} className="text-gray-800" />
                      </motion.button>
                      
                      {/* Next Button */}
                      <motion.button
                        onClick={handleNextImage}
                        className="h-10 w-10 flex items-center justify-center bg-white/90 text-gray-900 rounded-full shadow-lg hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-300 z-10"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Image suivante"
                      >
                        <Icon name="ChevronRight" size={24} className="text-gray-800" />
                      </motion.button>
                    </div>

                    {/* Image indicators */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(index);
                          }}
                          className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                            currentImageIndex === index 
                              ? 'bg-white w-6 scale-125' 
                              : 'bg-white/50 hover:bg-white/75'
                          }`}
                          aria-label={`Aller à l'image ${index + 1}`}
                        />
                      ))}
                    </div>

                    {/* Image counter */}
                    <div className="absolute top-4 right-4 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Product Details */}
              <div className="flex flex-col p-4 md:p-0">
                <div className="flex-grow">
                  <p className="text-sm text-muted-foreground mb-1">{selectedProduct.category}</p>
                  <h3 className="text-2xl font-bold text-foreground mb-3">{selectedProduct.title}</h3>
                  
                  <p className="text-md text-foreground mb-4">{selectedProduct.description}</p>

                  <div className="flex items-baseline mb-4">
                    <p className="text-3xl text-accent font-extrabold">
                      {selectedProduct.price.toFixed(3)} TND
                    </p>
                    {selectedProduct.originalPrice && (
                      <span className="ml-3 text-lg text-muted-foreground line-through">
                        {selectedProduct.originalPrice.toFixed(3)} TND
                      </span>
                    )}
                  </div>

                  {selectedProduct.inStock ? (
                    <p className="text-md font-semibold text-success mb-4">
                      <Icon name="CheckCircle" className="inline-block mr-2" />
                      En stock ({selectedProduct.stockCount} restants)
                    </p>
                  ) : (
                    <p className="text-md font-semibold text-error mb-4">
                      <Icon name="XCircle" className="inline-block mr-2" />
                      Rupture de stock
                    </p>
                  )}
                </div>

                {/* Sélection de la taille dans la boîte de dialogue */}
                {selectedProduct.availableSizes?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-foreground mb-3">TAILLE</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.availableSizes.map((size) => (
                        <button
                          key={size}
                          type="button"
                          className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                            selectedDialogSize === size
                              ? 'bg-accent text-white border-accent'
                              : 'bg-background text-foreground border-gray-200 hover:border-accent/50'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDialogSize(size);
                            setSelectedProduct(prev => ({
                              ...prev,
                              selectedSize: size
                            }));
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <Button 
                    variant="solid" 
                    size="lg"
                    className={`w-full transition-colors py-4 ${
                      isInCart(selectedProduct.id)
                        ? 'bg-gray-200 text-gray-500 cursor-default'
                        : 'bg-accent text-white hover:bg-accent/90'
                    }`}
                    onClick={(e) => {
                      if (isInCart(selectedProduct.id)) return;
                      // Mettre à jour la taille sélectionnée avant d'ajouter au panier
                      const productWithSize = {
                        ...selectedProduct,
                        selectedSize: selectedDialogSize || (selectedProduct.availableSizes?.[0] || 'UNIQUE')
                      };
                      handleAddToCart(productWithSize, e);
                    }}
                    disabled={!selectedProduct.inStock || addingProductId === selectedProduct.id || isInCart(selectedProduct.id)}
                  >
                    {addingProductId === selectedProduct.id ? (
                      <span className="flex items-center justify-center text-lg">
                        <Icon name="Loader" className="h-6 w-6 animate-spin mr-3" />
                        Ajout...
                      </span>
                    ) : isInCart(selectedProduct.id) ? (
                      <span className="flex items-center justify-center text-lg">
                        Déjà au panier
                      </span>
                    ) : (
                      <span className="flex items-center justify-center text-lg">
                        <Icon name="ShoppingBag" className="mr-3 h-6 w-6" />
                        Ajouter au panier
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Dialog>
      )}
    </>
  );
};

export default FeaturedCollections;